import React from 'react';
import { Plus, ArrowLeft, RefreshCw, Coins, Wallet } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { formatAmount } from '../utils/format';
import TransactionItem from '../components/TransactionItem';

const Dashboard = () => {
  const {
    accounts,
    transactions,
    loans,
    loading,
    setScreen,
    setSelectedAccount,
    setSelectedTransaction,
    setLoading,
    refreshData,
    fetchAccountTransactions,
    goToLoans,
    getAccountName,
    logout
  } = useApp();

  const totalBalance = accounts.reduce((sum, acc) => sum + (parseFloat(acc.balance) || 0), 0);

  const handleAccountClick = async (account) => {
    setSelectedAccount(account);
    setLoading(true);
    try {
      await fetchAccountTransactions(account.id);
    } finally {
      setLoading(false);
    }
    setScreen('accountDetail');
  };

  const handleTransactionClick = (tx) => {
    setSelectedTransaction(tx);
    setScreen('transactionDetail');
  };

  const handleRefresh = async () => {
    await refreshData();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 pb-20">
      {/* Header */}
      <div className="bg-indigo-600 text-white p-6 shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <div className="flex gap-2">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-2 hover:bg-white/10 rounded-lg transition"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={logout}
              className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition text-sm"
            >
              Logout
            </button>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 mb-4">
          <p className="text-indigo-200 text-sm mb-1">Total Balance</p>
          <p className="text-3xl font-bold">${formatAmount(totalBalance)}</p>
        </div>

        {/* Accounts Grid */}
        {accounts.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {accounts.map((account) => (
              <button
                key={account.id}
                onClick={() => handleAccountClick(account)}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-left hover:bg-white/20 transition"
              >
                <p className="text-indigo-200 text-xs mb-1">{account.name}</p>
                <p className="text-lg font-semibold">${formatAmount(parseFloat(account.balance))}</p>
                {account.bank && <p className="text-indigo-300 text-xs mt-1">{account.bank}</p>}
              </button>
            ))}
          </div>
        ) : (
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
            <p className="text-indigo-200">No accounts found</p>
          </div>
        )}
      </div>

      {/* Loans Section */}
      <div className="p-4">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-bold text-gray-800">Loans</h2>
          <button
            onClick={goToLoans}
            className="text-indigo-600 text-sm font-medium hover:text-indigo-800"
          >
            View All
          </button>
        </div>
        <button
          onClick={goToLoans}
          className="w-full bg-white rounded-xl p-4 shadow-sm flex items-center justify-between hover:bg-gray-50 transition"
        >
          <div className="flex items-center space-x-3">
            <div className="bg-amber-100 p-3 rounded-full">
              <Coins className="w-6 h-6 text-amber-600" />
            </div>
            <div className="text-left">
              <p className="font-medium text-gray-800">Manage Loans</p>
              <p className="text-sm text-gray-500">{loans.length} active loan{loans.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <ArrowLeft className="w-5 h-5 text-gray-400 transform rotate-180" />
        </button>
      </div>

      {/* Recent Transactions */}
      <div className="p-4 pt-0">
        <h2 className="text-lg font-bold text-gray-800 mb-3">Recent Transactions</h2>
        {transactions.length > 0 ? (
          <div className="space-y-2">
            {transactions.map((tx, idx) => (
              <TransactionItem
                key={tx.id || idx}
                transaction={tx}
                onClick={() => handleTransactionClick(tx)}
                showAccount={true}
                accountName={tx.accountName || getAccountName(tx.accountId)}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-xl p-8 text-center">
            <Wallet className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-400">No transactions yet</p>
            <p className="text-sm text-gray-400 mt-2">Tap the + button to add one</p>
          </div>
        )}
      </div>

      {/* FAB */}
      <button
        onClick={() => setScreen('add')}
        className="fixed bottom-6 right-6 bg-indigo-600 text-white p-4 rounded-full shadow-lg hover:bg-indigo-700 transition"
      >
        <Plus className="w-8 h-8" />
      </button>
    </div>
  );
};

export default Dashboard;
